/**
 * Профиль требований в интерфейсе: хранение в браузере и передача ссылкой.
 * В ссылку попадают стандарт и отличия от него, закодированные в #фрагменте —
 * он не отправляется на сервер даже при открытии ссылки.
 */

import { applyPatch, DEFAULT_PROFILE, diffProfile, getProfile, type DeepPartial, type Profile } from '@gostword/engine';

const STORAGE_KEY = 'gostword.profile.v1';
/** Необязательные параметры текста: «не проверять» хранится как null, иначе потерялось бы в JSON. */
const OPTIONAL_TEXT = ['sizePt', 'allowedSizesPt', 'lineSpacingAlt', 'firstLineIndentMaxCm', 'spacingBeforePt', 'spacingAfterPt'] as const;

type Raw = Record<string, unknown>;

const obj = (v: unknown): Raw => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : {});
const num = (v: unknown, min: number, max: number): number | undefined =>
  typeof v === 'number' && Number.isFinite(v) && v >= min && v <= max ? v : undefined;
const oneOf = <T extends string>(v: unknown, options: readonly T[], fallback: T): T =>
  (options as readonly unknown[]).includes(v) ? (v as T) : fallback;
const bool = (v: unknown, fallback: boolean): boolean => (typeof v === 'boolean' ? v : fallback);
const strings = (v: unknown, fallback: string[]): string[] =>
  Array.isArray(v) && v.length > 0 && v.length <= 10 && v.every((x) => typeof x === 'string' && x.length > 0 && x.length < 60)
    ? (v as string[])
    : fallback;
/** Ключ есть, но значение снято: null из ссылки или undefined из формы настроек. */
const cleared = (raw: Raw, key: string): boolean => key in raw && (raw[key] === null || raw[key] === undefined);
const optional = (raw: Raw, key: string, min: number, max: number, fallback: number | undefined): number | undefined =>
  !(key in raw) ? fallback : cleared(raw, key) ? undefined : (num(raw[key], min, max) ?? fallback);
function optionalList(raw: Raw, key: string, min: number, max: number, fallback: number[] | undefined): number[] | undefined {
  if (!(key in raw)) return fallback;
  if (cleared(raw, key)) return undefined;
  const v = raw[key];
  return Array.isArray(v) && v.length > 0 && v.length <= 6 && v.every((x) => num(x, min, max) !== undefined) ? (v as number[]) : fallback;
}

/** Стандарт, на котором основан профиль. */
export const baseOf = (profile: { id?: unknown }): Profile =>
  (typeof profile.id === 'string' && getProfile(profile.id)) || DEFAULT_PROFILE;

/** Профиль из недоверенного источника (ссылка, localStorage) — только известные поля с проверкой значений. */
export function sanitizeProfile(input: unknown): Profile {
  const r = obj(input);
  const d = baseOf(r);
  const page = obj(r.page);
  const margins = obj(page.margins);
  const pn = obj(r.pageNumbers);
  const text = obj(r.text);
  const headings = obj(r.headings);
  const captions = obj(r.captions);
  const refs = obj(r.references);
  const rules = obj(r.rules);
  return {
    ...d,
    page: {
      ...d.page,
      toleranceMm: num(page.toleranceMm, 0, 5) ?? d.page.toleranceMm,
      margins: {
        top: num(margins.top, 0, 80) ?? d.page.margins.top,
        bottom: num(margins.bottom, 0, 80) ?? d.page.margins.bottom,
        left: num(margins.left, 0, 80) ?? d.page.margins.left,
        right: num(margins.right, 0, 80) ?? d.page.margins.right,
      },
    },
    pageNumbers: {
      position: oneOf(pn.position, ['bottom', 'top'] as const, d.pageNumbers.position),
      align: oneOf(pn.align, ['center', 'right'] as const, d.pageNumbers.align),
      hideOnTitle: bool(pn.hideOnTitle, d.pageNumbers.hideOnTitle),
    },
    text: {
      fonts: strings(text.fonts, d.text.fonts),
      minSizePt: num(text.minSizePt, 8, 20) ?? d.text.minSizePt,
      sizePt: optional(text, 'sizePt', 8, 20, d.text.sizePt),
      allowedSizesPt: optionalList(text, 'allowedSizesPt', 8, 20, d.text.allowedSizesPt),
      lineSpacing: num(text.lineSpacing, 1, 3) ?? d.text.lineSpacing,
      lineSpacingAlt: optionalList(text, 'lineSpacingAlt', 1, 3, d.text.lineSpacingAlt),
      firstLineIndentCm: num(text.firstLineIndentCm, 0, 3) ?? d.text.firstLineIndentCm,
      firstLineIndentMaxCm: optional(text, 'firstLineIndentMaxCm', 0, 3, d.text.firstLineIndentMaxCm),
      alignment: oneOf(text.alignment, ['justify', 'left', 'any'] as const, d.text.alignment),
      spacingBeforePt: optional(text, 'spacingBeforePt', 0, 30, d.text.spacingBeforePt),
      spacingAfterPt: optional(text, 'spacingAfterPt', 0, 30, d.text.spacingAfterPt),
    },
    headings: {
      ...d.headings,
      structuralCase: oneOf(headings.structuralCase, ['upper', 'sentence'] as const, d.headings.structuralCase),
      structuralBold: bool(headings.structuralBold, d.headings.structuralBold),
      sectionAlign: oneOf(headings.sectionAlign, ['indent', 'center', 'left'] as const, d.headings.sectionAlign),
      sectionBold: bool(headings.sectionBold, d.headings.sectionBold),
      numberDot: bool(headings.numberDot, d.headings.numberDot),
      newPage: bool(headings.newPage, d.headings.newPage),
      tocDepth: num(headings.tocDepth, 1, 3) ?? d.headings.tocDepth,
    },
    captions: {
      dashes: strings(captions.dashes, d.captions.dashes),
      numbering: oneOf(captions.numbering, ['continuous', 'section', 'any'] as const, d.captions.numbering),
    },
    references: { order: oneOf(refs.order, ['appearance', 'alphabetical', 'any'] as const, d.references.order) },
    rules: {
      ...d.rules,
      ...(Object.fromEntries(
        Object.entries(rules).filter(([k, v]) => /^[a-z.-]{3,60}$/.test(k) && ['error', 'warning', 'info', 'off'].includes(v as string)),
      ) as Profile['rules']),
    },
  };
}

function withNulls(profile: Profile): Profile {
  const copy = structuredClone(profile) as unknown as { text: Raw };
  for (const key of OPTIONAL_TEXT) if (copy.text[key] === undefined) copy.text[key] = null;
  return copy as unknown as Profile;
}

/** Отличия от стандарта, на котором основан профиль. */
const patchOf = (profile: Profile): DeepPartial<Profile> => diffProfile(withNulls(baseOf(profile)), withNulls(profile));

/** Данные для ссылки и хранилища: стандарт (если он не основной) и отличия от него. */
function payloadOf(profile: Profile): Raw | undefined {
  const patch = patchOf(profile) as Raw;
  const isDefault = profile.id === DEFAULT_PROFILE.id;
  if (isDefault && !Object.keys(patch).length) return undefined;
  return isDefault ? patch : { id: profile.id, ...patch };
}

const fromPayload = (raw: unknown): Profile => sanitizeProfile(applyPatch(baseOf(obj(raw)), obj(raw) as DeepPartial<Profile>));

const toBase64Url = (s: string): string =>
  btoa(String.fromCharCode(...new TextEncoder().encode(s)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');

const fromBase64Url = (s: string): string => {
  const bin = atob(s.replace(/-/g, '+').replace(/_/g, '/'));
  return new TextDecoder().decode(Uint8Array.from(bin, (c) => c.charCodeAt(0)));
};

/** Требования отличаются от выбранного стандарта. */
export function isCustomized(profile: Profile): boolean {
  return Object.keys(patchOf(profile)).length > 0;
}

export function shareLink(profile: Profile): string {
  const url = new URL(location.href);
  const payload = payloadOf(profile);
  url.hash = payload ? `p=${toBase64Url(JSON.stringify(payload))}` : '';
  return url.toString();
}

export function loadProfile(): { profile: Profile; fromLink: boolean } {
  const m = /(?:^#|&)p=([A-Za-z0-9_-]+)/.exec(location.hash);
  if (m) {
    try {
      return { profile: fromPayload(JSON.parse(fromBase64Url(m[1]!))), fromLink: true };
    } catch {
      // Битая ссылка — просто используем стандарт по умолчанию.
    }
  }
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return { profile: fromPayload(JSON.parse(raw)), fromLink: false };
  } catch {
    // Хранилище недоступно (приватный режим) — не страшно.
  }
  return { profile: DEFAULT_PROFILE, fromLink: false };
}

export function saveProfile(profile: Profile): void {
  try {
    const payload = payloadOf(profile);
    if (payload) localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Не критично: профиль останется до перезагрузки страницы.
  }
}
